package redmcrosco.alex.farmatch.utils

import kotlinx.datetime.*
import redmcrosco.alex.farmatch.models.TariffaTipo

object TimeUtils {
    fun calculateTariffa(date: LocalDate, oraInizio: String): TariffaTipo {
        val isFestivo = isFestivo(date)
        val isNotturno = isNotturno(oraInizio)

        return when {
            isFestivo && isNotturno -> TariffaTipo.FESTIVO_NOTTURNO
            isFestivo -> TariffaTipo.FESTIVO_DIURNO
            isNotturno -> TariffaTipo.NOTTURNO
            else -> TariffaTipo.DIURNO
        }
    }

    fun isNotturno(oraInizio: String): Boolean {
        val hour = oraInizio.substringBefore(":").toIntOrNull() ?: return false
        // Richiesta: dalle 21:00 alle 09:00 è considerato Notturno
        return hour >= 21 || hour < 9
    }

    fun isFestivo(date: LocalDate): Boolean {
        if (date.dayOfWeek == DayOfWeek.SUNDAY) return true
        
        val day = date.dayOfMonth
        val month = date.monthNumber
        
        // Festività fisse in Italia
        if (month == 1 && (day == 1 || day == 6)) return true
        if (month == 4 && day == 25) return true
        if (month == 5 && day == 1) return true
        if (month == 6 && day == 2) return true
        if (month == 8 && day == 15) return true
        if (month == 11 && day == 1) return true
        if (month == 12 && (day == 8 || day == 25 || day == 26)) return true
        
        // Pasqua e Pasquetta
        val easter = calculateEaster(date.year)
        if (date == easter) return true
        
        val easterMonday = easter.plus(1, DateTimeUnit.DAY)
        if (date == easterMonday) return true
        
        return false
    }

    private fun calculateEaster(year: Int): LocalDate {
        val a = year % 19
        val b = year / 100
        val c = year % 100
        val d = b / 4
        val e = b % 4
        val f = (b + 8) / 25
        val g = (b - f + 1) / 3
        val h = (19 * a + b - d - g + 15) % 30
        val i = c / 4
        val k = c % 4
        val l = (32 + 2 * e + 2 * i - h - k) % 7
        val m = (a + 11 * h + 22 * l) / 451
        val month = (h + l - 7 * m + 114) / 31
        val day = ((h + l - 7 * m + 114) % 31) + 1
        return LocalDate(year, month, day)
    }

    fun formatCurrency(value: Double): String {
        val integerPartLong = value.toLong()
        val decimalPartInt = ((value - integerPartLong) * 100 + 0.5).toInt() // Arrotondamento a 2 cifre
        
        val integerPart = integerPartLong.toString().reversed().chunked(3).joinToString(".").reversed()
        val decimalPart = if (decimalPartInt < 10) "0$decimalPartInt" else decimalPartInt.toString().take(2)

        return "$integerPart,$decimalPart"
    }

    fun parseCurrency(value: String): Double {
        return value.replace(".", "").replace(",", ".").toDoubleOrNull() ?: 0.0
    }

    fun combineDateAndTime(dateInstant: Instant, timeStr: String): Instant {
        try {
            val date = dateInstant.toLocalDateTime(TimeZone.UTC).date
            val parts = timeStr.split(":")
            if (parts.size < 2) return dateInstant
            val hour = parts[0].trim().toInt()
            val min = parts[1].trim().take(2).toInt()
            return LocalDateTime(date.year, date.monthNumber, date.dayOfMonth, hour, min)
                .toInstant(TimeZone.currentSystemDefault())
        } catch (e: Exception) {
            return dateInstant
        }
    }

    fun generateDatesInRange(start: LocalDate, end: LocalDate, allowedDays: Set<DayOfWeek>): List<LocalDate> {
        val dates = mutableListOf<LocalDate>()
        var current = start
        while (current <= end) {
            if (allowedDays.isEmpty() || allowedDays.contains(current.dayOfWeek)) {
                dates.add(current)
            }
            current = current.plus(1, DateTimeUnit.DAY)
        }
        return dates
    }

    fun formatDate(date: LocalDate): String {
        val d = if (date.dayOfMonth < 10) "0${date.dayOfMonth}" else date.dayOfMonth.toString()
        val m = if (date.monthNumber < 10) "0${date.monthNumber}" else date.monthNumber.toString()
        return "$d/$m/${date.year}"
    }

    fun formatDate(instant: Instant, timeZone: TimeZone = TimeZone.UTC): String {
        return formatDate(instant.toLocalDateTime(timeZone).date)
    }

    fun formatDate(millis: Long): String {
        return formatDate(Instant.fromEpochMilliseconds(millis), TimeZone.currentSystemDefault())
    }

    fun getMonthYear(instant: Instant, timeZone: TimeZone = TimeZone.currentSystemDefault()): String {
        val ldt = instant.toLocalDateTime(timeZone)
        val monthName = when(ldt.month) {
            Month.JANUARY -> "Gennaio"
            Month.FEBRUARY -> "Febbraio"
            Month.MARCH -> "Marzo"
            Month.APRIL -> "Aprile"
            Month.MAY -> "Maggio"
            Month.JUNE -> "Giugno"
            Month.JULY -> "Luglio"
            Month.AUGUST -> "Agosto"
            Month.SEPTEMBER -> "Settembre"
            Month.OCTOBER -> "Ottobre"
            Month.NOVEMBER -> "Novembre"
            Month.DECEMBER -> "Dicembre"
            else -> ldt.month.name
        }
        return "$monthName ${ldt.year}"
    }

    fun isOverlap(
        dateA: Instant, startA: String, endA: String,
        dateB: Instant, startB: String, endB: String
    ): Boolean {
        val ldtA = dateA.toLocalDateTime(TimeZone.UTC).date
        val ldtB = dateB.toLocalDateTime(TimeZone.UTC).date
        if (ldtA != ldtB) return false
        
        // Formato orario atteso "HH:mm"
        // In Kotlin/JVM string comparison works for "HH:mm"
        return startA < endB && endA > startB
    }

    fun calculateHours(inizio: String, fine: String): Double {
        return try {
            val startParts = inizio.split(":")
            val endParts = fine.split(":")
            
            val startMinutes = startParts[0].toInt() * 60 + startParts[1].toInt()
            var endMinutes = endParts[0].toInt() * 60 + endParts[1].toInt()
            
            // Gestione turno che scavalca la mezzanotte
            if (endMinutes <= startMinutes) {
                endMinutes += 24 * 60
            }
            
            (endMinutes - startMinutes).toDouble() / 60.0
        } catch (e: Exception) {
            0.0
        }
    }
}
